import React, { useEffect, useMemo, useState } from "react";
import { Accordion, Card, Form, ButtonGroup, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faFileDownload,
} from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { TableEncargos } from "./TabelaEncargo";
import { TableProjecao } from "./TabelaProjecao";
import { exportEncargosExcel } from "../ExportEncargos/ExportEncargosExcel";

const AccordionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  margin-top: 16px;
  margin-bottom: 16px;
  border-radius: 5px;
  background-color: white;
`;

const AccordionNameSection = styled.span`
  display: flex;
  align-items: center;
`;

const AccordionTitle = styled.span`
  color: #212322;
  padding-left: 1em;
  font-weight: 600;
  font-size: 16px;
`;

export type EncargoViewType = "agent" | "unit";

export interface ProjectionPeriod {
  month: number;
  year: number;
}

export interface EncargosProps {
  projectionMonth: number;
  projectionYear: number;
  isCurrentMonth: boolean;
  isSuggestionAvailable: boolean;
  availableProjectionMonths: ProjectionPeriod[];
  byUnit: TaxHistoryData[];
  byAgent: TaxHistoryData[];
  projection?: TaxProjectionData[];
  projectionByUnit?: TaxProjectionData[];
  suggestion?: TaxSuggestionData[];
}

const MONTH_NAMES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const getMonthName = (month: number, year: number) =>
  month >= 1 && month <= 12
    ? `${MONTH_NAMES[month - 1]} de ${year}`
    : "Mês inválido";

export interface TaxHistoryData {
  name: string;
  agentName: string;
  companyBalance: number | null;
  lastBalanceUpdate: string | null;
  month: number;
  year: number;
  energyReserveTax: number;
  capacityReserveTax: number;
  quotaNuclearTax: number;
  financialGuaranteeTax: number;
  associativeContributionTax: number;
  mcpCreditTax: number;
  totalWithoutAssociative: number;
  totalChargesExcludingMcpCredit: number;
  measuringPointId?: string;
}

export interface TaxProjectionData {
  agentName: string;
  type: "Projeção";
  month: number;
  year: number;
  energyReserveTax: number;
  capacityReserveTax: number;
  quotaNuclearTax: number;
  financialGuaranteeTax: number;
  totalWithoutAssociative: number;
  totalChargesExcludingMcpCredit: number;
  name?: string;
  measuringPointId?: string;
}

export interface TaxSuggestionData {
  agentName: string;
  type: "Sugestão";
  month: number;
  year: number;
  amount: number;
}

export function EncargosAccordion({
  data,
  onProjectionPeriodChange,
  loading = false,
}: {
  data: EncargosProps;
  loading?: boolean;
  onProjectionPeriodChange?: (period: ProjectionPeriod) => void;
}) {
  const periods = useMemo(
    () =>
      Array.from(
        new Set(
          data.byAgent.map(
            (item) => `${item.year}-${String(item.month).padStart(2, "0")}`
          )
        )
      ).sort(),
    [data.byAgent]
  );

  const balances = useMemo(
    () =>
      Array.from(
        new Map(
          data.byAgent.map((item) => [
            item.agentName,
            {
              agentName: item.agentName,
              companyBalance: item.companyBalance,
              lastBalanceUpdate: item.lastBalanceUpdate,
            },
          ])
        ).values()
      ),
    [data.byAgent]
  );

  const lastPeriod = periods[periods.length - 1];
  const [selectedPeriod, setSelectedPeriod] = useState(lastPeriod);
  const [filter, setFilter] = useState<EncargoViewType>("unit");

  useEffect(() => {
    setSelectedPeriod(lastPeriod);
  }, [lastPeriod]);

  const projectionPeriodIsAvailable = data.availableProjectionMonths.some(
    ({ month, year }) =>
      month === data.projectionMonth && year === data.projectionYear
  );

  const source = filter === "agent" ? data.byAgent : data.byUnit;

  const filteredData = useMemo(
    () =>
      selectedPeriod
        ? source.filter(
            ({ year, month }) =>
              `${year}-${String(month).padStart(2, "0")}` === selectedPeriod
          )
        : source,
    [source, selectedPeriod]
  );

  return (
    <Accordion>
      <AccordionContainer>
        <Card style={{ width: "100%" }}>
          <Accordion.Toggle
            as={Card.Header}
            variant="link"
            eventKey="0"
            flush="true"
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              alignItems: "center",
              backgroundColor: "white",
              borderRadius: "5px 5px 5px 5px",
              paddingLeft: "2em",
              border: "1px solid #0072ce",
              minHeight: "70px",
            }}
          >
            <AccordionNameSection>
              <AccordionTitle>Histórico e projeção de encargos</AccordionTitle>
            </AccordionNameSection>
            <FontAwesomeIcon
              icon={faChevronDown}
              size="lg"
              style={{ marginRight: "1.2em" }}
            />
          </Accordion.Toggle>

          <Accordion.Collapse eventKey="0">
            <Card.Body>
              <div style={{ margin: "32px 0 16px 0" }}>
                <div
                  className="tableTitle d-flex flex-wrap justify-content-between align-items-center mb-3"
                  style={{ gap: "12px" }}
                >
                  <span className="clientDashboard-text fw-bold">
                    Histórico de encargos
                  </span>
                  <div
                    className="d-flex flex-wrap justify-content-center justify-content-md-start align-items-center"
                    style={{ gap: "12px" }}
                  >
                    <ButtonGroup className="order-1 order-md-2">
                      <Button
                        variant={
                          filter === "unit" ? "primary" : "outline-primary"
                        }
                        onClick={() => setFilter("unit")}
                      >
                        Por unidade
                      </Button>
                      <Button
                        variant={
                          filter === "agent" ? "primary" : "outline-primary"
                        }
                        onClick={() => setFilter("agent")}
                      >
                        Por agente
                      </Button>
                    </ButtonGroup>
                    <Button
                      variant="outline-primary"
                      className="order-2 order-md-3"
                      disabled={loading}
                      onClick={() => exportEncargosExcel(data, balances)}
                    >
                      <FontAwesomeIcon icon={faFileDownload} className="mr-2" />
                      Exportar dados
                    </Button>
                    <Form.Control
                      as="select"
                      className="select-chart order-3 order-md-1"
                      style={{
                        width: "auto",
                        height: "auto",
                        padding: "6px 8px",
                      }}
                      value={selectedPeriod}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setSelectedPeriod(e.target.value)
                      }
                    >
                      <option value="">Últimos 12 meses</option>

                      {periods.map((period) => {
                        const date = period.split("-");
                        return (
                          <option key={period} value={period}>
                            {getMonthName(parseInt(date[1]), parseInt(date[0]))}
                          </option>
                        );
                      })}
                    </Form.Control>
                  </div>
                </div>
                {filteredData.length > 0 ? (
                  <TableEncargos data={filteredData} type={filter} />
                ) : (
                  <p>Não há histórico de encargos para esta competência.</p>
                )}
                <div className="tableTitle d-flex flex-column align-items-center my-3">
                  <span
                    className="clientDashboard-text fw-bold text-center"
                    style={{ flex: 3, marginBottom: 0 }}
                  >
                    Projeção de encargos pagos em -{" "}
                    {getMonthName(data.projectionMonth, data.projectionYear)}
                  </span>
                  <span
                    className="text-muted text-center font-italic my-1"
                    style={{ fontSize: "12px" }}
                  >
                    Os valores projetados são uma estimativa e podem divergir do
                    encargo consolidado ao final do mês.
                  </span>
                </div>
                <Form.Group
                  controlId="projection-period"
                  className="d-flex flex-wrap justify-content-center align-items-center mb-4"
                  style={{ gap: "12px" }}
                >
                  <Form.Label className="mb-0">
                    Competência da projeção
                  </Form.Label>
                  <Form.Control
                    as="select"
                    style={{ width: "250px", maxWidth: "100%" }}
                    value={`${data.projectionYear}-${data.projectionMonth}`}
                    disabled={
                      loading ||
                      !onProjectionPeriodChange ||
                      data.availableProjectionMonths.length === 0
                    }
                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                      const [year, month] = event.target.value
                        .split("-")
                        .map(Number);
                      onProjectionPeriodChange?.({ month, year });
                    }}
                  >
                    {!projectionPeriodIsAvailable && (
                      <option
                        disabled
                        value={`${data.projectionYear}-${data.projectionMonth}`}
                      >
                        {getMonthName(
                          data.projectionMonth,
                          data.projectionYear
                        )}
                      </option>
                    )}
                    {data.availableProjectionMonths.map(({ month, year }) => (
                      <option
                        key={`${year}-${month}`}
                        value={`${year}-${month}`}
                      >
                        {getMonthName(month, year)}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
                {data.projection && data.projection.length > 0 && (
                  <>
                    <TableProjecao
                      key={`${data.projectionYear}-${data.projectionMonth}`}
                      isSuggestionAvailable={data.isSuggestionAvailable}
                      projection={data.projection}
                      projectionByUnit={data.projectionByUnit ?? []}
                      finalized={data.byAgent}
                      balances={balances}
                      suggestion={data.suggestion ?? []}
                    />
                  </>
                )}
              </div>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </AccordionContainer>
    </Accordion>
  );
}
